import { useChatbotAttachments } from 'features/chatbot'
import type { SearchStateUpdate } from 'features/knowledgeDocumentViewer/types'
import type { SensorSearchItem } from 'features/knowledgeDocumentViewer/types/searchableItems'
import { useCallback, useState } from 'react'

interface UseSensorResultsInteractionsParams {
  onOpenChatbot?: () => void
  onOpenSensorDashboard?: (sensor: SensorSearchItem) => void
  onSearchStateChange: (updates: SearchStateUpdate) => void
}

export function useSensorResultsInteractions({
  onOpenChatbot,
  onOpenSensorDashboard,
  onSearchStateChange,
}: UseSensorResultsInteractionsParams) {
  const [selectedSensor, setSelectedSensor] = useState<SensorSearchItem | null>(
    null
  )

  const { addAttachment, attachments } = useChatbotAttachments()

  const handleSensorClick = useCallback(
    (sensor: SensorSearchItem) => {
      setSelectedSensor(sensor)
      onSearchStateChange({ selectedDocumentId: null })
    },
    [onSearchStateChange]
  )

  const handleSensorDoubleClick = useCallback(
    (sensor: SensorSearchItem) => {
      onOpenSensorDashboard?.(sensor)
    },
    [onOpenSensorDashboard]
  )

  const handleCloseSensorPreview = useCallback(() => {
    setSelectedSensor(null)
  }, [])

  const handleSensorAddToContext = useCallback(
    (sensor: SensorSearchItem) => {
      const alreadyAttached = attachments.some(
        (attachment) =>
          attachment.type === 'dashboard' &&
          attachment.sensorIds?.includes(sensor.sensorId)
      )
      if (!alreadyAttached) {
        addAttachment({
          id: `sensor-${sensor.sensorId}`,
          type: 'dashboard',
          name: sensor.name,
          displayTitle: sensor.name,
          sensorIds: [sensor.sensorId],
        })
      }
      onOpenChatbot?.()
    },
    [addAttachment, attachments, onOpenChatbot]
  )

  return {
    selectedSensor,
    handleSensorClick,
    handleSensorDoubleClick,
    handleCloseSensorPreview,
    handleSensorAddToContext,
  }
}
